import {
  parseStyleFile,
  STYLE_MODELS_MAX,
  styleFileOf,
  type StudioChoice,
  type StyleModel,
} from '@base/shared';
import { Download, FolderOpen, GitCompare, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Panel } from '../components/ui.tsx';
import { sameStudio } from './state.ts';

function download(name: string, studio: StudioChoice) {
  const blob = new Blob([styleFileOf(name, studio)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name.replace(/[^\p{L}\p{N}-]+/gu, '-').toLowerCase() || 'estilo'}.estilo.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const newId = () => `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

/**
 * Meus modelos (ADR 0133, parte 5): guardar a escolha com um nome, abrir, comparar, apagar, e
 * levar num arquivo para outro sistema. Sem `onModelsChange` (quem não grava), só o arquivo.
 */
export function ModelsPanel({
  value,
  onChange,
  models,
  onModelsChange,
  onCompare,
  readOnly,
}: {
  value: StudioChoice;
  onChange: (next: StudioChoice) => void;
  models: StyleModel[];
  onModelsChange?: (next: StyleModel[]) => Promise<unknown>;
  onCompare: (m: StyleModel) => void;
  readOnly: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string }>();
  const file = useRef<HTMLInputElement>(null);
  const canSave = !readOnly && !!onModelsChange;
  const write = async (next: StyleModel[], text: string) => {
    setBusy(true);
    try {
      await onModelsChange!(next);
      setMessage({ ok: true, text });
    } catch {
      setMessage({ ok: false, text: t('style.models.failed') });
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    const clean = name.trim();
    if (!clean) return;
    const model = { id: newId(), name: clean, studio: value, savedAt: new Date().toISOString() };
    void write(
      [model, ...models].slice(0, STYLE_MODELS_MAX),
      t('style.models.saved', { name: clean }),
    );
    setName('');
  };
  const load = async (f: File | undefined) => {
    if (!f) return;
    const read = parseStyleFile(await f.text());
    if (!read.ok) return setMessage({ ok: false, text: t(`style.models.bad_${read.reason}`) });
    onChange(read.studio);
    setName(read.name);
    setMessage({ ok: true, text: t('style.models.imported', { name: read.name }) });
  };
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' });
  const icon =
    'flex size-8 items-center justify-center rounded-md text-muted hover:bg-tonal hover:text-ink';
  return (
    <Panel title={t('style.models.title')}>
      <p className="text-sm text-muted">{t('style.models.hint')}</p>
      {canSave && (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Input
            value={name}
            maxLength={60}
            aria-label={t('style.models.name')}
            placeholder={t('style.models.name')}
            onChange={(e) => setName(e.target.value)}
          />
          <span className="shrink-0 whitespace-nowrap">
            <Button type="submit" disabled={busy || !name.trim()}>
              {t('style.models.save')}
            </Button>
          </span>
        </form>
      )}
      {models.length > 0 && (
        <ul className="flex flex-col divide-y divide-rule rounded-md border border-rule">
          {models.map((m) => {
            const open = sameStudio(m.studio, value);
            return (
              <li key={m.id} className="flex items-center gap-1 px-3 py-2 text-sm">
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className={`truncate ${open ? 'font-semibold' : ''}`}>{m.name}</span>
                  <span className="text-xs text-muted">{date.format(new Date(m.savedAt))}</span>
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    className={icon}
                    title={t('style.models.open')}
                    aria-label={`${t('style.models.open')}: ${m.name}`}
                    onClick={() => onChange(m.studio)}
                  >
                    <FolderOpen size={15} aria-hidden />
                  </button>
                )}
                <button
                  type="button"
                  className={icon}
                  title={t('style.models.compare')}
                  aria-label={`${t('style.models.compare')}: ${m.name}`}
                  onClick={() => onCompare(m)}
                >
                  <GitCompare size={15} aria-hidden />
                </button>
                <button
                  type="button"
                  className={icon}
                  title={t('style.models.export')}
                  aria-label={`${t('style.models.export')}: ${m.name}`}
                  onClick={() => download(m.name, m.studio)}
                >
                  <Download size={15} aria-hidden />
                </button>
                {canSave && (
                  <button
                    type="button"
                    className={icon}
                    disabled={busy}
                    title={t('style.models.remove')}
                    aria-label={`${t('style.models.remove')}: ${m.name}`}
                    onClick={() =>
                      void write(
                        models.filter((x) => x.id !== m.id),
                        t('style.models.removed', { name: m.name }),
                      )
                    }
                  >
                    <Trash2 size={15} aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() => download(name.trim() || t('style.models.file'), value)}
        >
          <Download size={16} aria-hidden />
          {t('style.models.exportThis')}
        </Button>
        {!readOnly && (
          <>
            <input
              ref={file}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              tabIndex={-1}
              onChange={(e) => {
                void load(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button variant="secondary" onClick={() => file.current?.click()}>
              <Upload size={16} aria-hidden />
              {t('style.models.import')}
            </Button>
          </>
        )}
      </div>
      {message && (
        <p role="status" className={`text-sm ${message.ok ? 'text-ok' : 'text-danger'}`}>
          {message.text}
        </p>
      )}
    </Panel>
  );
}
